import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import {
  CircularProgress,
  Switch,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useStyles } from '../../styles/styles';
import StyledCard from '../../components/cards/StyledCard';
import StyledCardContent from '../../components/cards/StyledCardContent';
import StyledCardHeader from '../../components/cards/StyledCardHeader';
import { drawerStore } from '../../state/drawerStore';
import { FormInputTypes } from '../../utils/constants';
import { Api } from '../../api';
import { greenColor } from '../../components/main/react/App';
import ActionsList from '../../components/other/ActionsList';
import connectorIcon from '../../images/PricingIcons/connector_icon.png';
import { useProvider } from '../../state/providerStore';
import { getPriceListDropdownInput } from '../Locations/LocationDetailsOverview';
import { priceListDetailsRoute } from '../../utils/routing';
import attachFileIcon from '../../images/PricingIcons/attach_file.png';
import accountBoxIcon from '../../images/PricingIcons/account_box.png';
import calendarClockIcon from '../../images/PricingIcons/calendar_clock.png';

const ChargerDetailsPricing = observer(() => {
  const classes = useStyles();
  const { chargerid } = useParams<{ chargerid: string }>();
  const history = useHistory();
  const [connectors, setConnectors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const catalogsProvider = useProvider(Api.catalog.listCatalogs);

  const getCatalogInfo = (catalogId: string) => {
    let catalog = catalogsProvider.data?.find((c: any) => c.id === catalogId);
    if (!catalog) {
      for (const parent of catalogsProvider.data || []) {
        const child = parent.children?.find((c: any) => c.id === catalogId);
        if (child) {
          catalog = child;
          break;
        }
      }
    }
    return {
      name: catalog?.name || catalogId,
      productsCount: catalog?.productsCount ?? catalog?.products?.length ?? 0,
      isFree: catalog?.isFree || false,
      isUserRestricted: catalog?.isUserRestricted || false,
      isTimeRestricted: catalog?.isTimeRestricted || false,
    };
  };

  const loadPricing = async (showSpinner = true) => {
    try {
      if (showSpinner) {
        setLoading(true);
      }
      const { data } = await Api.charge.getConnectorsWithPricingForChargePoint(chargerid);
      setConnectors(data?.data || data || []);
    } catch (err) {
      console.error('Failed to load pricing', err);
    } finally {
      setLoading(false);
    }
  };

  const updateConnectorLocally = (connectorId: string, updates: any) => {
    setConnectors((prev) => prev.map((cc) => (cc.id === connectorId ? { ...cc, ...updates } : cc)));
  };

  useEffect(() => {
    loadPricing();
  }, [chargerid]);

  const onClickAssignPriceList = () => {
    const allConnectorIds = connectors.map((cc: any) => cc.id);

    const ConnectorPicker = observer(() => {
      const selectedIds: string[] = toJS(drawerStore.values)?.selectedConnectorIds || [];

      const toggleConnector = (connectorId: string) => {
        const current: string[] = toJS(drawerStore.values)?.selectedConnectorIds || [];
        const next = current.includes(connectorId)
          ? current.filter((id) => id !== connectorId)
          : [...current, connectorId];
        drawerStore.setValues({ ...toJS(drawerStore.values), selectedConnectorIds: next });
      };

      const selectAll = () => {
        drawerStore.setValues({
          ...toJS(drawerStore.values),
          selectedConnectorIds: [...allConnectorIds],
        });
      };

      const removeConnector = (connectorId: string) => {
        const current: string[] = toJS(drawerStore.values)?.selectedConnectorIds || [];
        drawerStore.setValues({
          ...toJS(drawerStore.values),
          selectedConnectorIds: current.filter((id) => id !== connectorId),
        });
      };

      return (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <span style={{ color: '#888', fontSize: 14 }}>Assign to</span>
            <Button
              variant="text"
              size="small"
              style={{ color: greenColor, fontWeight: 600, textTransform: 'none', fontSize: 13 }}
              onClick={selectAll}
            >
              Select all
            </Button>
          </div>

          {connectors.map((cc: any) => (
            <FormControlLabel
              key={cc.id}
              control={
                <Checkbox
                  checked={selectedIds.includes(cc.id)}
                  onChange={() => toggleConnector(cc.id)}
                  sx={{ '&.Mui-checked': { color: greenColor } }}
                  size="small"
                />
              }
              label={<span style={{ fontSize: 14 }}>{cc.ocppId}</span>}
              style={{ display: 'flex', marginLeft: 0 }}
            />
          ))}

          {selectedIds.length > 0 && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: 8, marginTop: 8 }}>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>
                Selected ({selectedIds.length})
              </div>
              {selectedIds.map((id) => {
                const cc = connectors.find((c: any) => c.id === id);
                return (
                  <div
                    key={id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '4px 0',
                    }}
                  >
                    <span style={{ fontSize: 14 }}>{cc?.ocppId || id}</span>
                    <IconButton size="small" onClick={() => removeConnector(id)}>
                      <CloseIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    });

    drawerStore.openDrawer({
      label: 'Assign Price list',
      inputs: [
        {
          name: 'isFree',
          label: 'Is Free',
          type: FormInputTypes.switch,
        },
        getPriceListDropdownInput(catalogsProvider, []),
        {
          name: 'connectorPicker',
          label: '',
          type: FormInputTypes.component,
        },
      ],
      clickCallback: async () => {
        const values = toJS(drawerStore.values);
        const selectedConnectorIds: string[] = values.selectedConnectorIds || [];

        if (selectedConnectorIds.length > 0) {
          for (const connectorId of selectedConnectorIds) {
            await Api.charge.updateConnector(connectorId, { isFree: values.isFree || false });
          }
        }

        if (values.catalogId && selectedConnectorIds.length > 0) {
          await Api.charge.batchAddCatalogToConnectors(selectedConnectorIds, values.catalogId);
        }

        loadPricing();
      },
      buttonLabel: 'ASSIGN',
    });

    drawerStore.setValues({
      isFree: false,
      catalogId: '',
      selectedConnectorIds: [],
      connectorPicker: <ConnectorPicker />,
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <CircularProgress style={{ color: greenColor }} />
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <Button
          variant="text"
          size="small"
          style={{ color: greenColor, fontWeight: 600, textTransform: 'none' }}
          onClick={onClickAssignPriceList}
        >
          ASSIGN PRICE LIST
        </Button>
      </div>

      {connectors.map((cc: any, connIdx: number) => {
        const catalogs = cc.catalogs || [];

        return (
          <div
            key={cc.id}
            style={{
              marginBottom: '1.5rem',
              border: connIdx > 0 ? '1px dashed #ccc' : undefined,
              borderRadius: 8,
            }}
          >
            <StyledCard>
              <StyledCardHeader title={`Connector ${connIdx + 1}`} />
              <StyledCardContent>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #eee', color: '#888', fontSize: 13 }}>
                      <th style={{ textAlign: 'left', padding: '8px', fontWeight: 400 }}>
                        OCPP ID
                      </th>
                      <th
                        style={{ textAlign: 'center', padding: '8px', fontWeight: 400, width: 80 }}
                      >
                        Is Free
                      </th>
                      <th style={{ textAlign: 'left', padding: '8px', fontWeight: 400 }}>
                        Price Lists
                      </th>
                      <th />
                      <th style={{ width: 40 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {catalogs.length > 0 ? (
                      catalogs.map((cat: any, idx: number) => (
                        <tr key={`${cc.id}-${idx}`} style={{ borderBottom: '1px solid #f5f5f5' }}>
                          {idx === 0 && (
                            <>
                              <td
                                rowSpan={catalogs.length}
                                style={{ padding: '8px', verticalAlign: 'top' }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <img
                                    src={connectorIcon}
                                    alt="Connector"
                                    style={{ width: 24, height: 24 }}
                                  />
                                  <div>
                                    <div style={{ color: greenColor, fontWeight: 500 }}>
                                      {cc.ocppId}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#888' }}>
                                      {cc.name} ({cc.type})/
                                      {(() => {
                                        const w = parseInt(
                                          cc.chargePoint?.actualMaxPower ||
                                            cc.chargePoint?.maxPower ||
                                            '0',
                                          10,
                                        );
                                        return w >= 1000 ? `${(w / 1000).toFixed(0)} kW` : `${w} W`;
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td
                                rowSpan={catalogs.length}
                                style={{
                                  textAlign: 'center',
                                  verticalAlign: 'top',
                                  padding: '8px',
                                }}
                              >
                                <Switch
                                  checked={cc.isFree === true}
                                  onChange={async (e) => {
                                    const checked = e.target.checked;
                                    updateConnectorLocally(cc.id, { isFree: checked });
                                    await Api.charge.updateConnector(cc.id, { isFree: checked });
                                  }}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: greenColor },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: greenColor,
                                    },
                                  }}
                                />
                              </td>
                            </>
                          )}
                          <td style={{ padding: '8px' }}>
                            <span style={{ color: '#888', marginRight: 8 }}>
                              {catalogs.length - idx}.
                            </span>
                            <div style={{ display: 'inline-block' }}>
                              <div
                                style={{ color: greenColor, fontWeight: 500, cursor: 'pointer' }}
                                onClick={() => history.push(priceListDetailsRoute(cat.catalogId))}
                              >
                                {getCatalogInfo(cat.catalogId).name}
                              </div>
                              <div style={{ fontSize: 12, color: '#888' }}>
                                {getCatalogInfo(cat.catalogId).productsCount} products
                              </div>
                            </div>
                          </td>
                          <td
                            style={{
                              padding: '8px',
                              whiteSpace: 'nowrap',
                              verticalAlign: 'middle',
                            }}
                          >
                            {getCatalogInfo(cat.catalogId).isFree && (
                              <img
                                src={attachFileIcon}
                                alt="Attachment"
                                style={{ width: 20, height: 20, marginRight: 6 }}
                              />
                            )}
                            {getCatalogInfo(cat.catalogId).isUserRestricted && (
                              <img
                                src={accountBoxIcon}
                                alt="User restricted"
                                style={{ width: 20, height: 20, marginRight: 6 }}
                              />
                            )}
                            {getCatalogInfo(cat.catalogId).isTimeRestricted && (
                              <img
                                src={calendarClockIcon}
                                alt="Time restricted"
                                style={{ width: 20, height: 20, marginRight: 6 }}
                              />
                            )}
                          </td>
                          <td style={{ padding: '8px' }}>
                            <ActionsList
                              actions={[
                                {
                                  label: 'Remove',
                                  callback: async () => {
                                    await Api.charge.batchDeleteCatalogFromConnectors(
                                      [cc.id],
                                      cat.catalogId,
                                    );
                                    loadPricing();
                                  },
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr style={{ borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <img
                              src={connectorIcon}
                              alt="Connector"
                              style={{ width: 24, height: 24 }}
                            />
                            <div>
                              <div style={{ color: greenColor, fontWeight: 500 }}>{cc.ocppId}</div>
                              <div style={{ fontSize: 12, color: '#888' }}>
                                {cc.name} ({cc.type})/
                                {(() => {
                                  const w = parseInt(
                                    cc.chargePoint?.actualMaxPower ||
                                      cc.chargePoint?.maxPower ||
                                      '0',
                                    10,
                                  );
                                  return w >= 1000 ? `${(w / 1000).toFixed(0)} kW` : `${w} W`;
                                })()}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px' }}>
                          <Switch
                            checked={cc.isFree === true}
                            onChange={async (e) => {
                              const checked = e.target.checked;
                              updateConnectorLocally(cc.id, { isFree: checked });
                              await Api.charge.updateConnector(cc.id, { isFree: checked });
                            }}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: greenColor },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: greenColor,
                              },
                            }}
                          />
                        </td>
                        <td style={{ padding: '8px', color: '#aaa', fontStyle: 'italic' }}>
                          No price lists assigned
                        </td>
                        <td />
                        <td />
                      </tr>
                    )}
                  </tbody>
                </table>
              </StyledCardContent>
            </StyledCard>
          </div>
        );
      })}

      {connectors.length === 0 && (
        <div style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>
          No connectors found for this charge point.
        </div>
      )}
    </div>
  );
});

export default ChargerDetailsPricing;
